import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';

/**
 * Export utilities for generating presentations and reports
 */

// Export formats
export const EXPORT_FORMATS = {
  PDF: 'pdf',
  PNG: 'png',
  JPEG: 'jpeg'
};

// Ranking options for agent leaderboard
export const RANKING_OPTIONS = {
  TOTAL_POINTS: { value: 'totalPoints', label: 'Overall Score', field: 'totalPoints' },
  DEALS_COMPLETED: { value: 'dealsCompleted', label: 'Deals Completed', field: 'dealsCompleted' },
  DEAL_VALUE: { value: 'dealValue', label: 'Deal Value', field: 'dealValue' },
  COMMISSION_VALUE: { value: 'commissionValue', label: 'Commission Earned', field: 'commissionValue' },
  LISTINGS_ACQUIRED: { value: 'listingsAcquired', label: 'Listings Acquired', field: 'listingsAcquired' },
  ACTIVITIES_COMPLETED: { value: 'activitiesCompleted', label: 'Activities Completed', field: 'activitiesCompleted' },
  RESPONSE_TIME: { value: 'avgTimeToFirstAction', label: 'Response Time', field: 'avgTimeToFirstAction', sortDirection: 'asc' },
  FIRST_ACTION_COUNT: { value: 'firstActionCount', label: 'Lead Actions', field: 'firstActionCount' }
};

// Presentation themes
export const PRESENTATION_THEMES = {
  PROFESSIONAL: {
    name: 'Professional',
    primaryColor: '#854fff',
    secondaryColor: '#6c757d',
    backgroundColor: '#ffffff',
    textColor: '#333333',
    accentColor: '#f8f9fa'
  },
  MODERN: {
    name: 'Modern',
    primaryColor: '#4361ee',
    secondaryColor: '#7209b7',
    backgroundColor: '#ffffff',
    textColor: '#2d3436',
    accentColor: '#e9ecef'
  },
  CORPORATE: {
    name: 'Corporate',
    primaryColor: '#2c3e50',
    secondaryColor: '#34495e',
    backgroundColor: '#ffffff',
    textColor: '#2c3e50',
    accentColor: '#ecf0f1'
  }
};

/**
 * Capture DOM element as canvas
 * @param {HTMLElement} element - DOM element to capture
 * @param {Object} options - html2canvas options
 * @returns {Promise<HTMLCanvasElement>}
 */
export const captureElementAsCanvas = async (element, options = {}) => {
  const defaultOptions = {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
    ...options
  };

  try {
    const canvas = await html2canvas(element, defaultOptions);
    return canvas;
  } catch (error) {
    console.error('Error capturing element as canvas:', error);
    throw new Error('Failed to capture element as image');
  }
};

/**
 * Convert canvas to blob
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {string} format - Image format (png, jpeg)
 * @param {number} quality - Image quality (0-1)
 * @returns {Promise<Blob>}
 */
export const canvasToBlob = (canvas, format = 'png', quality = 0.9) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      `image/${format}`,
      quality
    );
  });
};

/**
 * Export element as image
 * @param {HTMLElement} element - DOM element to export
 * @param {string} filename - Output filename
 * @param {string} format - Image format
 * @param {Object} options - Export options
 */
export const exportAsImage = async (element, filename, format = 'png', options = {}) => {
  try {
    const canvas = await captureElementAsCanvas(element, options.canvasOptions);
    const blob = await canvasToBlob(canvas, format, options.quality || 0.9);
    
    saveAs(blob, `${filename}.${format}`);
    return { success: true, message: `Exported as ${format.toUpperCase()} successfully` };
  } catch (error) {
    console.error('Error exporting as image:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Export element as PDF
 * @param {HTMLElement} element - DOM element to export
 * @param {string} filename - Output filename
 * @param {Object} options - PDF options
 */
export const exportAsPDF = async (element, filename, options = {}) => {
  try {
    const canvas = await captureElementAsCanvas(element, options.canvasOptions);
    
    const pdf = new jsPDF({
      orientation: options.orientation || 'landscape',
      unit: 'mm',
      format: options.format || 'a4',
      ...options.pdfOptions
    });

    const imgData = canvas.toDataURL('image/png');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    // Calculate dimensions to fit the page while maintaining aspect ratio
    const canvasAspectRatio = canvas.width / canvas.height;
    const pdfAspectRatio = pdfWidth / pdfHeight;
    
    let imgWidth, imgHeight, xOffset = 0, yOffset = 0;
    
    if (canvasAspectRatio > pdfAspectRatio) {
      // Canvas is wider than PDF page
      imgWidth = pdfWidth;
      imgHeight = pdfWidth / canvasAspectRatio;
      yOffset = (pdfHeight - imgHeight) / 2;
    } else {
      // Canvas is taller than PDF page
      imgHeight = pdfHeight;
      imgWidth = pdfHeight * canvasAspectRatio;
      xOffset = (pdfWidth - imgWidth) / 2;
    }

    pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);
    pdf.save(`${filename}.pdf`);
    
    return { success: true, message: 'Exported as PDF successfully' };
  } catch (error) {
    console.error('Error exporting as PDF:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Sort agents by ranking criteria
 * @param {Array} agents - Array of agent data
 * @param {string} rankingKey - Ranking criteria key
 * @returns {Array} Sorted agents array
 */
export const sortAgentsByRanking = (agents, rankingKey) => {
  if (!agents || !Array.isArray(agents)) return [];
  
  const rankingOption = RANKING_OPTIONS[rankingKey];
  if (!rankingOption) return agents;
  
  const { field, sortDirection = 'desc' } = rankingOption;
  
  return [...agents].sort((a, b) => {
    const aValue = a[field] || 0;
    const bValue = b[field] || 0;
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });
};

/**
 * Generate filename with timestamp
 * @param {string} baseName - Base filename
 * @param {string} rankingType - Ranking type for filename
 * @returns {string} Generated filename
 */
export const generateFilename = (baseName, rankingType) => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
  const ranking = RANKING_OPTIONS[rankingType]?.label.replace(/\s+/g, '_') || 'Overall_Score';
  return `${baseName}_${ranking}_${timestamp}`;
};

/**
 * Prepare chart for export by temporarily modifying styles
 * @param {HTMLElement} chartElement - Chart container element
 * @returns {Function} Cleanup function
 */
export const prepareChartForExport = (chartElement) => {
  if (!chartElement) return () => {};
  
  const originalStyles = {
    backgroundColor: chartElement.style.backgroundColor,
    padding: chartElement.style.padding
  };
  
  // Apply export-friendly styles
  chartElement.style.backgroundColor = '#ffffff';
  chartElement.style.padding = '20px';
  
  // Return cleanup function
  return () => {
    chartElement.style.backgroundColor = originalStyles.backgroundColor;
    chartElement.style.padding = originalStyles.padding;
  };
};

/**
 * Create a temporary container for export with custom styling
 * @param {HTMLElement} sourceElement - Source element to clone
 * @param {Object} theme - Theme configuration
 * @returns {Object} Container element and cleanup function
 */
export const createExportContainer = (sourceElement, theme = PRESENTATION_THEMES.PROFESSIONAL) => {
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    top: -10000px;
    left: -10000px;
    width: 1200px;
    background-color: ${theme.backgroundColor};
    color: ${theme.textColor};
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    padding: 40px;
    box-sizing: border-box;
  `;
  
  // Clone the source element
  const clonedElement = sourceElement.cloneNode(true);
  container.appendChild(clonedElement);
  document.body.appendChild(container);
  
  const cleanup = () => {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };
  
  return { container, cleanup };
};
